import apiClient from './apiClient';

const apiBaseURL = '/api/chatbots';

export interface ShortcutButton {
  label: string;
  prompt: string;
  template_id?: string | null;
}

export type ChatbotVisibility = 'private' | 'organization';

export interface Chatbot {
  id: string;
  user_id: string;
  organization_id: string | null;
  domain_id: string | null;
  name: string;
  description: string | null;
  system_prompt: string | null;
  is_default: boolean;
  visibility: ChatbotVisibility;
  theme_filters: string[] | null;
  folder_paths: string[] | null;
  is_out_of_scope_enabled: boolean;
  is_malicious_enabled: boolean;
  apply_source_type_and_theme_weighting_enabled: boolean;
  shortcut_buttons: ShortcutButton[] | null;
  allowed_template_ids: string[] | null;
  created_at: string;
}

export interface ChatbotCreate {
  name: string;
  description?: string;
  system_prompt?: string;
  organization_id?: string;
  domain_id?: string;
  is_default?: boolean;
  visibility?: ChatbotVisibility;
  theme_filters?: string[];
  folder_paths?: string[];
  is_out_of_scope_enabled?: boolean;
  is_malicious_enabled?: boolean;
  apply_source_type_and_theme_weighting_enabled?: boolean;
  shortcut_buttons?: ShortcutButton[] | null;
  allowed_template_ids?: string[] | null;
}

export interface ChatbotUpdate {
  name?: string;
  description?: string;
  system_prompt?: string;
  organization_id?: string;
  domain_id?: string;
  visibility?: ChatbotVisibility;
  theme_filters?: string[];
  folder_paths?: string[];
  is_out_of_scope_enabled?: boolean;
  is_malicious_enabled?: boolean;
  apply_source_type_and_theme_weighting_enabled?: boolean;
  shortcut_buttons?: ShortcutButton[] | null;
  allowed_template_ids?: string[] | null;
}

export interface ThemeDefinition {
  code: string;
  label?: string;
  description?: string;
  icon?: string;
}

export interface ChatbotThemeOptionsResponse {
  valid_themes: ThemeDefinition[];
  domain_entity_mapping: Record<string, string[]>;
  base_types_always_included: string[];
}

export const chatbotsService = {
  /**
   * List user's chatbots
   */
  list: async (): Promise<Chatbot[]> => {
    const response = await apiClient.get(`${apiBaseURL}/`);
    return response.data;
  },

  /**
   * Get user's default chatbot
   */
  getDefault: async (): Promise<Chatbot> => {
    const response = await apiClient.get(`${apiBaseURL}/default`);
    return response.data;
  },

  /**
   * Get specific chatbot
   */
  get: async (id: string): Promise<Chatbot> => {
    const response = await apiClient.get(`${apiBaseURL}/${id}`);
    return response.data;
  },

  /**
   * Create new chatbot
   */
  create: async (data: ChatbotCreate): Promise<Chatbot> => {
    const response = await apiClient.post(`${apiBaseURL}/`, data);
    return response.data;
  },

  /**
   * Update chatbot
   */
  update: async (id: string, data: ChatbotUpdate): Promise<Chatbot> => {
    const response = await apiClient.put(`${apiBaseURL}/${id}`, data);
    return response.data;
  },

  /**
   * Set chatbot as default
   */
  setAsDefault: async (id: string): Promise<Chatbot> => {
    const response = await apiClient.post(`${apiBaseURL}/${id}/set-default`);
    return response.data;
  },

  /**
   * Delete chatbot
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${apiBaseURL}/${id}`);
  },

  /**
   * Get available themes/mappings for chatbot filters.
   * If no themes are configured, returns empty arrays.
   */
  getThemeOptions: async (): Promise<ChatbotThemeOptionsResponse> => {
    const response = await apiClient.get(`${apiBaseURL}/theme-options`);
    const payload = response.data || {};

    return {
      valid_themes: Array.isArray(payload.valid_themes) ? payload.valid_themes : [],
      domain_entity_mapping:
        payload.domain_entity_mapping && typeof payload.domain_entity_mapping === 'object'
          ? payload.domain_entity_mapping
          : {},
      base_types_always_included: Array.isArray(payload.base_types_always_included)
        ? payload.base_types_always_included
        : [],
    };
  },
};
